import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ShieldOff, ArrowLeft, Send, CheckCircle } from "lucide-react";
import axios from "axios";
import { API } from "@/lib/api";

/**
 * Page /compte-bloque
 *
 * Accessible sans authentification normale. Deux cas d'entrée :
 *  1. Depuis le formulaire de connexion (login bloqué) → state passé via navigate()
 *     avec { support_token, user_email, user_phone, user_name }
 *  2. Depuis l'intercepteur API (session expirée + compte bloqué en cours de navigation)
 *     → pas de contexte, page affichée mais formulaire désactivé (token expiré).
 */
export default function BlockedAccount() {
  const navigate = useNavigate();
  const location = useLocation();

  // Contexte passé depuis Login via navigate("/compte-bloque", { state: {...} })
  const ctx = location.state || {};
  const { support_token, user_email, user_phone, user_name } = ctx;

  const [phone, setPhone] = useState(user_phone || "");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [ticketId, setTicketId] = useState(null);
  const [error, setError] = useState(null);
  const [alreadyExists, setAlreadyExists] = useState(false);

  // Si on arrive ici sans state (intercepteur ou navigation directe),
  // on propose de retourner à la connexion pour obtenir un support_token frais.
  const hasContext = Boolean(support_token);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!phone.trim()) {
      setError("Le numéro de téléphone est obligatoire.");
      return;
    }
    if (!message.trim() || message.trim().length < 10) {
      setError("Veuillez décrire votre situation (minimum 10 caractères).");
      return;
    }
    setSending(true);
    setError(null);
    try {
      const { data } = await axios.post(
        `${API}/support/tickets/blocked-account`,
        { message: message.trim(), contact_phone: phone.trim() || undefined },
        { headers: { Authorization: `Bearer ${support_token}` } }
      );
      setSent(true);
      setTicketId(data.ticket_id);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "object" && detail?.code === "ACCOUNT_BLOCKED_TICKET_ALREADY_EXISTS") {
        setAlreadyExists(true);
      } else if (err.response?.status === 401) {
        setError("Votre session de support a expiré. Veuillez vous reconnecter pour obtenir un nouveau lien.");
      } else {
        setError(
          typeof detail === "string"
            ? detail
            : "Une erreur est survenue. Veuillez réessayer."
        );
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f4ede4] flex flex-col">
      {/* Header minimal */}
      <header className="bg-[#1c1814] py-4 px-6">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <span className="font-serif text-2xl text-white tracking-wide">
            Lumière<span className="text-[#b8895c]">.</span>
          </span>
          <button
            onClick={() => navigate("/connexion")}
            className="flex items-center gap-1.5 text-[#b8895c] text-xs uppercase tracking-widest hover:text-white transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Connexion
          </button>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {sent ? (
            /* ── Confirmation après envoi ── */
            <div className="bg-white border border-[#e8e1d4] rounded-lg p-8 text-center space-y-4">
              <CheckCircle className="h-12 w-12 text-emerald-500 mx-auto" strokeWidth={1.5} />
              <h1 className="font-serif text-2xl text-[#1c1814]">Demande envoyée</h1>
              <p className="text-[#3d342a] text-sm leading-relaxed">
                Votre demande a bien été enregistrée. Notre équipe va l'examiner dans les meilleurs délais.
              </p>
              {ticketId && (
                <p className="text-xs text-[#8a7d6b] font-mono bg-[#f4ede4] rounded px-3 py-2">
                  Référence : #{ticketId.slice(-8).toUpperCase()}
                </p>
              )}
              <p className="text-xs text-[#8a7d6b]">
                Un email de confirmation vous a été envoyé à <strong>{user_email}</strong>.
              </p>
            </div>
          ) : alreadyExists ? (
            /* ── Ticket déjà existant ── */
            <div className="bg-white border border-[#e8e1d4] rounded-lg p-8 text-center space-y-4">
              <ShieldOff className="h-12 w-12 text-[#b8895c] mx-auto" strokeWidth={1.5} />
              <h1 className="font-serif text-2xl text-[#1c1814]">Demande déjà en cours</h1>
              <p className="text-[#3d342a] text-sm leading-relaxed">
                Une demande de support est déjà en cours pour votre compte. Notre équipe l'examine et reviendra vers vous.
              </p>
              <p className="text-xs text-[#8a7d6b]">
                Si vous n'avez pas reçu de réponse sous 48h, contactez-nous directement.
              </p>
            </div>
          ) : (
            /* ── Formulaire principal ── */
            <div className="bg-white border border-[#e8e1d4] rounded-lg p-8 space-y-6">
              <div className="text-center space-y-2">
                <ShieldOff className="h-10 w-10 text-[#b8895c] mx-auto" strokeWidth={1.5} />
                <h1 className="font-serif text-2xl text-[#1c1814]">Votre compte est bloqué</h1>
                <p className="text-[#3d342a] text-sm leading-relaxed">
                  L'accès à votre compte est actuellement suspendu. Si vous pensez
                  qu'il s'agit d'une erreur, contactez notre support ci-dessous.
                </p>
              </div>

              {!hasContext ? (
                /* Pas de support_token disponible */
                <div className="space-y-4">
                  <p className="text-sm text-[#8a7d6b] text-center leading-relaxed">
                    Pour contacter le support, reconnectez-vous afin d'obtenir un lien de contact valide.
                  </p>
                  <button
                    onClick={() => navigate("/connexion")}
                    className="w-full bg-[#1c1814] text-white py-3 text-xs uppercase tracking-[0.15em] hover:bg-[#3d342a] transition-colors"
                  >
                    Aller à la connexion
                  </button>
                </div>
              ) : (
                /* Formulaire avec support_token valide */
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Email — lecture seule */}
                  <div>
                    <label className="text-xs uppercase tracking-[0.12em] text-[#8a7d6b] block mb-1">
                      Email
                    </label>
                    <div className="bg-[#f4ede4] border border-[#e8e1d4] rounded px-3 py-2 text-sm text-[#3d342a] truncate">
                      {user_email || "—"}
                    </div>
                  </div>

                  {/* Téléphone — lecture seule si déjà enregistré, saisissable sinon */}
                  <div>
                    <label className="text-xs uppercase tracking-[0.12em] text-[#8a7d6b] block mb-1">
                      Téléphone <span className="text-red-400">*</span>
                    </label>
                    {user_phone ? (
                      <div className="bg-[#f4ede4] border border-[#e8e1d4] rounded px-3 py-2 text-sm text-[#3d342a]">
                        {user_phone}
                      </div>
                    ) : (
                      <>
                        <input
                          type="tel"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          placeholder="Ex : 06 12 34 56 78"
                          className="w-full border border-[#e8e1d4] rounded px-3 py-2 text-sm text-[#1c1814] bg-white focus:outline-none focus:border-[#b8895c] transition-colors"
                          required
                        />
                        <p className="text-xs text-[#8a7d6b] mt-1">
                          Aucun numéro enregistré sur votre compte — veuillez le saisir.
                        </p>
                      </>
                    )}
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-[0.12em] text-[#8a7d6b] block mb-1">
                      Sujet
                    </label>
                    <div className="bg-[#f4ede4] border border-[#e8e1d4] rounded px-3 py-2 text-sm text-[#3d342a]">
                      Compte bloqué
                    </div>
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-[0.12em] text-[#8a7d6b] block mb-1.5">
                      Votre message <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      rows={5}
                      placeholder="Expliquez pourquoi vous pensez que le blocage est une erreur..."
                      className="w-full border border-[#e8e1d4] rounded px-3 py-2 text-sm text-[#1c1814] bg-white resize-none focus:outline-none focus:border-[#b8895c] transition-colors"
                      required
                    />
                    <p className="text-xs text-[#8a7d6b] mt-1">Minimum 10 caractères</p>
                  </div>

                  {error && (
                    <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={sending || !phone.trim() || !message.trim()}
                    className="w-full bg-[#1c1814] text-white py-3 text-xs uppercase tracking-[0.15em] hover:bg-[#3d342a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <Send className="h-3.5 w-3.5" />
                    {sending ? "Envoi en cours…" : "Envoyer ma demande"}
                  </button>

                  <p className="text-xs text-[#8a7d6b] text-center">
                    Votre mot de passe ne vous sera jamais demandé.
                  </p>
                </form>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
